import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const UserSearchDropdown = ({ value, onChange, placeholder = "Search for a user..." }) => {
    const [searchQuery, setSearchQuery] = useState(value);
    const [searchResults, setSearchResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Update search query when value prop changes
    useEffect(() => {
        setSearchQuery(value);
        if (!value) {
            setSelectedUser(null);
        }
    }, [value]);

    // Search users with debouncing
    useEffect(() => {
        const searchUsers = async () => {
            if (searchQuery.trim().length < 2) {
                setSearchResults([]);
                setShowDropdown(false);
                return;
            }

            setSearchLoading(true);
            try {
                const response = await axios.get(`/api/profile/search?q=${encodeURIComponent(searchQuery)}`);
                setSearchResults(response.data.users || []);
                setShowDropdown(true);
            } catch (error) {
                console.error('Search error:', error);
                setSearchResults([]);
            } finally {
                setSearchLoading(false);
            }
        };

        const debounceTimer = setTimeout(searchUsers, 300);
        return () => clearTimeout(debounceTimer);
    }, [searchQuery]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setSearchQuery(newValue);
        onChange(newValue);
        setSelectedUser(null);
        if (newValue.trim().length >= 2) {
            setShowDropdown(true);
        }
    };

    const handleUserSelect = (user) => {
        setSearchQuery(user.username);
        onChange(user.username);
        setSelectedUser(user);
        setShowDropdown(false);
        inputRef.current?.blur();
    };

    const handleInputFocus = () => {
        if (searchQuery.trim().length >= 2 && searchResults.length > 0) {
            setShowDropdown(true);
        }
    };

    return (
        <div style={{ position: 'relative' }} ref={dropdownRef}>
            <div style={{ position: 'relative' }}>
                <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    placeholder={placeholder}
                    style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        backgroundColor: 'rgba(148, 163, 184, 0.1)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '8px',
                        color: '#e2e8f0',
                        fontSize: '1rem',
                        outline: 'none'
                    }}
                />
                {searchLoading && (
                    <div style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#94a3b8'
                    }}>
                        ⏳
                    </div>
                )}
            </div>

            {showDropdown && searchResults.length > 0 && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--card-bg, #1e293b)',
                    border: '1px solid var(--border-color, #334155)',
                    borderRadius: '8px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    marginTop: '4px'
                }}>
                    {searchResults.map((user) => (
                        <div
                            key={user._id || user.username}
                            onClick={() => handleUserSelect(user)}
                            style={{
                                padding: '0.75rem 1rem',
                                cursor: 'pointer',
                                borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.1)'}
                            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                            {user.profile?.avatar?.url ? (
                                <img
                                    src={user.profile.avatar.url}
                                    alt={user.username}
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                />
                            ) : (
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#3b82f6',
                                    fontWeight: 'bold',
                                    fontSize: '0.875rem'
                                }}>
                                    {user.username.charAt(0).toUpperCase()}
                                </div>
                            )}
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    color: '#e2e8f0',
                                    fontWeight: '500',
                                    fontSize: '0.95rem'
                                }}>
                                    {user.username}
                                </div>
                                {user.accountType && (
                                    <div style={{
                                        color: '#94a3b8',
                                        fontSize: '0.8rem',
                                        textTransform: 'capitalize'
                                    }}>
                                        {user.accountType}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showDropdown && searchQuery.trim().length >= 2 && searchResults.length === 0 && !searchLoading && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--card-bg, #1e293b)',
                    border: '1px solid var(--border-color, #334155)',
                    borderRadius: '8px',
                    padding: '1rem',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    zIndex: 1000,
                    marginTop: '4px',
                    color: '#94a3b8',
                    textAlign: 'center',
                    fontSize: '0.9rem'
                }}>
                    No users found
                </div>
            )}
        </div>
    );
};

export default UserSearchDropdown;